import type { ReactNode } from 'react';
import { PageContainer } from '../PageContainer/PageContainer';
import { TemplateSection } from './Section';

export type LandingTemplateProps = {
  /** A Hero (as="h1") with the live eyebrow, the highlighted clause and the SearchField. */
  hero: ReactNode;
  /** The quick FilterChips with their counts, and the "How we count" link. */
  quickFilters?: ReactNode;
  /** The coverage panels (from Finland, from abroad), with logo tiles sized to each logo. */
  coverage?: ReactNode;
  /**
   * The routes into the rest of the product: one `<li>` per route (Card plus link). With no visible
   * title, `label` names the navigation landmark instead.
   */
  routes?: { title?: ReactNode; label?: string; content: ReactNode };
  /** What a JobCrawls listing tells you, with the illustrative listing. */
  listing?: { title: ReactNode; eyebrow?: ReactNode; description?: ReactNode; action?: ReactNode; content: ReactNode };
  /** A page-specific section between the listing and the guides (the landing's application tracker): a TemplateSection. */
  afterListing?: ReactNode;
  /** Guides, as ArticleCards. */
  guides?: { title: ReactNode; eyebrow?: ReactNode; description?: ReactNode; content: ReactNode; action?: ReactNode };
  /** Below the guide cards: the plain link directory of every other guide. */
  afterGuides?: ReactNode;
  /** Questions, as an Accordion, with its heading. The landing page's FAQPage JSON-LD needs it visible. */
  faq?: { title: ReactNode; eyebrow?: ReactNode; content: ReactNode };
  className?: string;
};

/**
 * The home page. Count the yellow: only the search button and the highlighted clause; everything
 * else is ink, white and the employers' own logos. Counts come from the landing snapshot.
 */
export function LandingTemplate({
  hero,
  quickFilters,
  coverage,
  routes,
  listing,
  afterListing,
  guides,
  afterGuides,
  faq,
  className,
}: LandingTemplateProps) {
  return (
    <PageContainer width="wide" className={className}>
      {/* Chapters, not sections (2026-10-14): the homepage reads top to bottom, so its sections sit
          a chapter apart (64px on phones to 96px), each heading a block above its content. */}
      <div className="flex flex-col gap-jc-chapter pb-jc-chapter">
        <div className="flex flex-col gap-5">
          {hero}
          {quickFilters ? <div className="flex flex-wrap items-center gap-2">{quickFilters}</div> : null}
        </div>
        {coverage ? <div className="grid gap-5 lg:grid-cols-2">{coverage}</div> : null}
        {routes ? (
          <TemplateSection id="routes" title={routes.title} spacing="lg">
            <nav aria-label={routes.label}>
              <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0 lg:grid-cols-5 lg:gap-4">{routes.content}</ul>
            </nav>
          </TemplateSection>
        ) : null}
        {listing ? (
          <TemplateSection
            id="listing"
            title={listing.title}
            eyebrow={listing.eyebrow}
            description={listing.description}
            action={listing.action}
            spacing="lg"
          >
            {listing.content}
          </TemplateSection>
        ) : null}
        {afterListing}
        {guides ? (
          <TemplateSection
            id="guides"
            title={guides.title}
            eyebrow={guides.eyebrow}
            description={guides.description}
            action={guides.action}
            spacing="lg"
          >
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{guides.content}</div>
            {afterGuides}
          </TemplateSection>
        ) : null}
        {faq ? (
          // Two columns from lg: the title on the left, the questions filling the right, so the section
          // spans the page like the others instead of leaving its right half empty.
          <section id="faq" aria-labelledby="faq-title" className="grid scroll-mt-24 gap-jc-block lg:grid-cols-[1fr_2fr] lg:gap-jc-section">
            <div className="flex flex-col gap-1">
              {faq.eyebrow ? <span className="type-eyebrow text-jc-ink-secondary">{faq.eyebrow}</span> : null}
              <h2 id="faq-title" className="type-title text-jc-ink">
                {faq.title}
              </h2>
            </div>
            <div className="min-w-0">{faq.content}</div>
          </section>
        ) : null}
      </div>
    </PageContainer>
  );
}
